import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usersApi } from "@/api/users.api";
import { queryKeys } from "@/utils/queryKeys";
import type { ListQuery } from "@/types/common";
import type { AccountStatus } from "@/types/domain";

export function useUsersList(query: ListQuery & { status?: AccountStatus }) {
  return useQuery({
    queryKey: queryKeys.users.list(query),
    queryFn: () => usersApi.list(query),
    placeholderData: (prev) => prev,
  });
}

/** Includes lifetimeSpend, paidRides and recentRides — no separate ride-history call needed. */
export function useUser(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.users.detail(id ?? ""),
    queryFn: () => usersApi.getById(id as string),
    enabled: !!id,
  });
}

export function useSetUserStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, reason }: { id: string; status: AccountStatus; reason?: string }) =>
      usersApi.setStatus(id, status, reason),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: ["users", "list"] });
    },
  });
}
